import {memo, useEffect} from "react";
import {Alert, Card, Col, Divider, Drawer, Row, Spin} from "antd";
import useSWR from "swr";
import {LoadingOutlined} from "@ant-design/icons";
import SkillsBarChart from "../Charts/SkillsBarChart";
import CompareRadarChart from "../Charts/CompareRadarChart";
import pluck from "../Helpers/ArrayHelper";
import GrowthRateChart from "../Charts/GrowthRateChart";
import IndustryChart from "../Charts/IndustryChart";
import UniversityChart from "../Charts/UniversityChart";
import ColumnCompareAbleChart from "../Charts/ColumnCompareChart";

const CareerDrawer = memo(({careerId, visible = false, onClose = (() => {})}) => {

    const {data: career, error} = useSWR(careerId ? `/careers/${careerId}` : null);

    if(error)
        return <Alert
            message="กำลังปรับปรุงข้อมูล"
            description="ข้อมูลชุดนี้อยู่ระหว่างการปรับปรุง กรุณาลองอีกครั้งภายหลัง"
            type="warning"
            showIcon
            style={{ marginTop: '24px' }}
        />

    return <Drawer
        title={career?.name ? `ตำแหน่งงาน ${career?.name}` : 'กำลังดาวน์โหลด'}
        visible={visible}
        onClose={onClose()}
        placement={'bottom'}
        height="calc(100% - 64px)"
        destroyOnClose
    >
        {!career ? <Row justify={'center'}>
            <Col>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            </Col>
        </Row> : <>
            {
                (!career?.thailand_skills.length > 0 ||
                !career?.thailand_skills_growth.length > 0 ||
                !career?.thailand_industries.length > 0 ||
                !career?.thailand_universities.length > 0) &&
                    <Alert
                        type='warning'
                        message='ไม่พบข้อมูลกลุ่มตัวอย่างของตำแหน่งงานในประเทศไทย'
                        description={'ไม่พบข้อมูลกลุ่มตัวอย่างของตำแหน่งงานในประเทศไทย'}
                        style={{ marginTop: '12px', marginBottom: '12px' }}
                        showIcon
                    />
            }
            <h2>
                เปรียบเทียบ Skill
            </h2>
            <Row gutter={8}>
                <Col span={24} lg={!!career?.thailand_skills.length ? 12 : 24}>
                    <Card
                        title={`จำนวนคนที่มีความสามารถด้านต่าง ๆ จากตำแหน่งงาน ${career?.name} (ทั่วโลก)`}
                    >
                        <SkillsBarChart
                            data={career?.skills}
                            xAxis={'skill'}
                            yAxis={'amount'}
                            customConfig={{
                                meta: {
                                    amount: { alias: 'จำนวนคน', formatter: (v) => `${v} คน` },
                                },
                                color: '#164176'
                            }}
                        />
                    </Card>
                </Col>
                {!!career?.thailand_skills.length && <Col span={24} lg={12}>
                    <Card
                        title={`จำนวนคนที่มีความสามารถด้านต่าง ๆ จากตำแหน่งงาน ${career?.name} (ประเทศไทย)`}
                    >
                        <SkillsBarChart
                            data={career?.thailand_skills}
                            xAxis={'skill'}
                            yAxis={'amount'}
                            customConfig={{
                                meta: {
                                    amount: {alias: 'จำนวนคน', formatter: (v) => `${v} คน`},
                                },
                                color: '#ff9318'
                            }}
                        />
                    </Card>
                </Col>}
            </Row>
            <Divider/>
            { !!career?.thailand_skills.length &&
            <>
                <Row>
                    <Col span={24}>
                        <Card
                            title={`เปรียบเทียบอัตราส่วนผู้ที่ความสามารถในด้านต่าง ๆ จากตำแหน่งงาน ${career?.name} จากทั่วโลก กับ ประเทศไทย`}
                        >
                            <ColumnCompareAbleChart
                                data={[
                                    ...career?.skills?.map((skill) => {
                                        return {
                                            skill: skill.skill,
                                            amount: skill.percentage,
                                            scope: 'ทั่วโลก'
                                        };
                                    }),
                                    ...career?.thailand_skills?.map((skill) => {
                                        return {
                                            skill: skill.skill,
                                            amount: skill.percentage,
                                            scope: 'ประเทศไทย'
                                        };
                                    }),
                                ]}
                                xAxis={'skill'}
                                yAxis={'amount'}
                                customConfig={{
                                    seriesField: 'scope',
                                    meta: {
                                        amount: { alias: 'ร้อยละ', formatter: (v) => `${v}%` }
                                    },
                                    isGroup: true,
                                    scrollbar: { type: 'horizontal' },
                                    color: ['#164176', '#dd720b']
                                }}
                            />
                        </Card>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Card>
                            <CompareRadarChart
                                data={
                                    (() => {
                                        let all_skills = [...new Set(pluck([...career?.skills, ...career?.thailand_skills], 'skill'))]
                                        let plot_data = [];
                                        all_skills.forEach((skill) => {
                                            let a = career?.skills.filter(s_skill => s_skill.skill === skill)
                                            let b = career?.thailand_skills.filter(s_skill => s_skill.skill === skill)
                                            if(a.length > 0 && b.length > 0) {
                                                a = a[0];
                                                b = b[0];
                                                plot_data.push({
                                                    skill: a.skill,
                                                    percentage: a.percentage,
                                                    scope: 'ทั่วโลก'
                                                });
                                                plot_data.push({
                                                    skill: b.skill,
                                                    percentage: b.percentage,
                                                    scope: 'ประเทศไทย'
                                                });
                                            }
                                        })
                                        return plot_data.splice(0, 20)
                                    })()
                                }
                            />
                        </Card>
                    </Col>
                </Row>
            </>
            }
            <h1>
                ข้อมูลเพิ่มเติม
            </h1>
            <h2>
                ทั่วโลก
            </h2>
            <Row gutter={8}>
                <Col span={24} lg={12}>
                    <Card
                        title={`ทักษะในตำแหน่งงาน ${career?.name} ที่มีอัตราการเพิ่มมากที่สุดในช่วง 1 ปี`}
                    >
                        <GrowthRateChart
                            data={career?.skills_growth}
                        />
                    </Card>
                </Col>
                <Col span={24} lg={12}>
                    <Card
                        title={`กลุ่มอุตสาหกรรมที่มีตำแหน่งงาน ${career?.name} มากที่สุด`}
                    >
                        <IndustryChart data={career?.industries} />
                    </Card>
                </Col>
            </Row>
            <Divider/>
            {
                (!!career?.thailand_skills_growth.length ||
                !!career?.thailand_industries.length ||
                !!career?.thailand_universities.length) &&
                    <>
                        <h2>
                            ประเทศไทย
                        </h2>
                        <Row gutter={8}>
                            <Col span={24} lg={12}>
                                <Card
                                    title={`ทักษะในตำแหน่งงาน ${career?.name} ที่มีอัตราการเพิ่มมากที่สุดในช่วง 1 ปี`}
                                >
                                    <GrowthRateChart
                                        data={career?.thailand_skills_growth}
                                    />
                                </Card>
                            </Col>
                            <Col span={24} lg={12}>
                                <Card
                                    title={`กลุ่มอุตสาหกรรมที่มีตำแหน่งงาน ${career?.name} มากที่สุด`}
                                >
                                    <IndustryChart
                                        data={career?.thailand_industries}
                                    />
                                </Card>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '24px' }}>
                            <Col span={24}>
                                <Card
                                    title={`มหาวิทยาลัยที่มีการผลิตบุคลากรในตำแหน่งงาน ${career?.name} มากที่สุด`}
                                >
                                    <UniversityChart data={career?.thailand_universities} />
                                </Card>
                            </Col>
                        </Row>
                    </>
            }
        </>}
    </Drawer>
});

export default CareerDrawer;
